import {
  Avatar,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  HStack,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { RiPencilLine } from "react-icons/ri";
import { useAppState } from "../../../../components/App/AppProvider";
import { useApiQuery } from "../../../../utilities/apibelRequest";
import { formatDateOrIsoString } from "../../../../utilities/dateUtils";
import { EditManualSectionOwnerModal } from "./EditManualSectionOwnerModal";

type Props = {
  sectionID: string;
};

export default function ManualDetailsCard({ sectionID }: Props) {
  const { permissions } = useAppState().app;

  const updateOwnerModal = useDisclosure({ defaultIsOpen: false });
  const sectionQuery = useApiQuery(
    "manual/sectionByID",
    {
      manualSectionID: sectionID || "",
    },
    { enabled: !!sectionID },
  );

  const isOwn =
    sectionQuery.data?.responsibleUser.userID === permissions.INFO.userID;

  const canEdit =
    permissions.MANUAL.UPDATE.ALL || (permissions.MANUAL.UPDATE.SELF && isOwn);

  const handleOpenEditOwner = () => {
    updateOwnerModal.onOpen();
  };
  const handleCancelEditOwner = () => {
    updateOwnerModal.onClose();
  };

  return (
    <Box pb={6}>
      <Card variant="outline">
        <CardHeader>
          <Text>Section Summary</Text>
        </CardHeader>
        <CardBody>
          {sectionQuery.isSuccess && (
            <>
              <VStack
                align="left"
                pb={6}
                _focusWithin={{
                  ".hover-buttons": {
                    visibility: "visible",
                    opacity: 1,
                  },
                }}
                _hover={{
                  ".hover-buttons": {
                    visibility: "visible",
                    opacity: 1,
                  },
                }}>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.500">
                    Section Owner:
                  </Text>
                  <Tooltip
                    label={
                      !canEdit
                        ? "You don't have permission to edit this"
                        : undefined
                    }>
                    <Button
                      size="sm"
                      variant="ghost"
                      visibility="hidden"
                      className="hover-buttons"
                      opacity={0}
                      isDisabled={!canEdit}
                      leftIcon={<RiPencilLine size={16} />}
                      onClick={handleOpenEditOwner}>
                      Edit
                    </Button>
                  </Tooltip>
                </HStack>
                <Text>
                  <Avatar
                    size="xs"
                    name={`${sectionQuery.data.responsibleUser.firstName} ${sectionQuery.data.responsibleUser.lastName}`}
                  />{" "}
                  {`${sectionQuery.data.responsibleUser.firstName} ${sectionQuery.data.responsibleUser.lastName}`}
                </Text>
              </VStack>

              <VStack align="left">
                <Text fontSize="sm" color="gray.500">
                  Last Updated:
                </Text>
                <Text>
                  {formatDateOrIsoString(
                    sectionQuery.data.modifiedTs,
                    "datetime-short",
                  )}
                </Text>
              </VStack>
            </>
          )}
        </CardBody>
      </Card>
      {sectionID && sectionQuery.isSuccess && (
        <EditManualSectionOwnerModal
          manualSectionID={sectionID}
          isOpen={updateOwnerModal.isOpen}
          onClose={handleCancelEditOwner}
          manualSectionResponsibleUser={{
            firstName: sectionQuery.data.responsibleUser.firstName,
            lastName: sectionQuery.data.responsibleUser.lastName,
            userID: sectionQuery.data.responsibleUser.userID,
          }}
        />
      )}
    </Box>
  );
}
